import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { Observable, of, shareReplay, tap } from "rxjs";
import { environment } from "../environments/environment";
import { AiInputDataResponse, TosAcceptanceRequest, TosAcceptanceResponse, UserDetailsResponse } from "../models/ai-input-data.model";
import { GeneratedMediaPlanResponse, GenerateMediaPlannerBody, GetMediaPlanResponse, MediaPlansLightResponse } from "../models/generate-media-plan.model";

@Injectable({
    providedIn: 'root',
})
export class GenericUtilService {
    private readonly httpClient = inject(HttpClient);
    public userDetailsSignal: WritableSignal<UserDetailsResponse | null> = signal(null);

    // Cache for AI input data (static data that doesn't change frequently)
    private readonly aiInputDataCache: WritableSignal<AiInputDataResponse | null> = signal(null);

    getAiInputData(): Observable<AiInputDataResponse> {
        // Return cached data if available
        const cachedData = this.aiInputDataCache();
        if (cachedData) {
            return of(cachedData);
        }

        // Fetch and cache the data
        return this.httpClient.get<AiInputDataResponse>(`${environment.baseURL}/ai-input-data`).pipe(
            tap((data: AiInputDataResponse) => this.aiInputDataCache.set(data))
        );
    }

    getCityLocations(query: string, country_code: string): Observable<string[]> {
        let params = new HttpParams();
        params = params.append('query', query);
        params = params.append('country_code', country_code);
        return this.httpClient.get<string[]>(`${environment.baseURL}/locations/autocomplete`, { params })
    }

    private userDetailsCache$?: Observable<UserDetailsResponse>;

    getUserDetails(): Observable<UserDetailsResponse> {
        this.userDetailsCache$ ??= this.httpClient.get<UserDetailsResponse>(`${environment.baseURL}/users/me`).pipe(
            tap((userData: UserDetailsResponse) => this.userDetailsSignal.set(userData)),
            shareReplay(1)
        );
        return this.userDetailsCache$;
    }

    generatePlan(body: GenerateMediaPlannerBody): Observable<GeneratedMediaPlanResponse> {
        return this.httpClient.post<GeneratedMediaPlanResponse>(`${environment.baseURL}/generate-plan`, body)
    }

    getMediaPlan(plan_id: string): Observable<GetMediaPlanResponse> {
        return this.httpClient.get<GetMediaPlanResponse>(`${environment.baseURL}/media-plans/${plan_id}`);
    }

    getAllMediaPlans(): Observable<MediaPlansLightResponse[]> {
        return this.httpClient.get<MediaPlansLightResponse[]>(`${environment.baseURL}/media-plans`);
    }

    downloadMediaPlan(plan_id: string): Observable<Blob> {
        return this.httpClient.get(`${environment.baseURL}/media-plans/${plan_id}/download`, {
            responseType: 'blob'
        });
    }

    deleteMediaPlan(plan_id: string): Observable<void> {
        return this.httpClient.delete<void>(`${environment.baseURL}/media-plans/${plan_id}`);
    }

    acceptTermsOfService(body: TosAcceptanceRequest): Observable<TosAcceptanceResponse> {
        return this.httpClient.post<TosAcceptanceResponse>(`${environment.baseURL}/users/accept-tos`, body);
    }

}